"use client"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Plus, Minus } from "lucide-react"
import { faqItems } from "@/features/home/data"
import { staggerContainer, fadeUp, sectionLabel } from "@/lib/motion"

const VP = { once: true, amount: 0.05 }

export function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section id="sss" className="relative py-16 sm:py-24 lg:py-32" aria-label="Sık sorulan sorular">
      <div className="brand-divider" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 max-w-6xl">

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
        >
          <motion.span variants={sectionLabel} className="text-primary font-medium text-sm uppercase tracking-wider inline-block">
            Sık Sorulan Sorular
          </motion.span>
          <motion.h2 variants={fadeUp} className="font-[family-name:var(--font-heading)] text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-4 sm:mb-6 text-foreground">
            Aklınızdaki <span className="text-gradient-brand">Sorular</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            En çok merak edilen konular. Cevabını bulamazsanız iletişime geçin.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="max-w-3xl mx-auto space-y-3"
        >
          {faqItems.map((item, index) => {
            const isOpen = openIndex === index
            const panelId = `faq-panel-${index}`
            const triggerId = `faq-trigger-${index}`
            return (
              <motion.div
                key={index}
                variants={fadeUp}
                className="glass hairline rounded-2xl overflow-hidden"
                style={{ transform: "translateZ(0)", willChange: "transform" }}
              >
                <button
                  id={triggerId}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 sm:py-5 text-left active:bg-secondary/30 transition-colors"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <span className="font-[family-name:var(--font-heading)] font-semibold text-foreground text-sm sm:text-base">
                    {item.question}
                  </span>
                  <div className="shrink-0 w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <AnimatePresence mode="wait" initial={false}>
                      {isOpen ? (
                        <motion.span
                          key="minus"
                          initial={{ rotate: 45, opacity: 0 }}
                          animate={{ rotate: 0, opacity: 1 }}
                          exit={{ rotate: -45, opacity: 0 }}
                          transition={{ duration: 0.15 }}
                        >
                          <Minus className="w-3.5 h-3.5" aria-hidden="true" />
                        </motion.span>
                      ) : (
                        <motion.span
                          key="plus"
                          initial={{ rotate: -45, opacity: 0 }}
                          animate={{ rotate: 0, opacity: 1 }}
                          exit={{ rotate: 45, opacity: 0 }}
                          transition={{ duration: 0.15 }}
                        >
                          <Plus className="w-3.5 h-3.5" aria-hidden="true" />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={panelId}
                      role="region"
                      aria-labelledby={triggerId}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="overflow-hidden"
                      style={{ willChange: "height, opacity" }}
                    >
                      <p className="px-5 sm:px-6 pb-5 text-muted-foreground text-sm sm:text-base leading-relaxed border-t border-border/40 pt-4">
                        {item.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )
          })}
        </motion.div>

      </div>
    </section>
  )
}
